import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { 
  Shield, Users, Scissors, Building2, Sparkles, 
  CheckCircle2, Circle, Lock, ArrowRight, ArrowLeft,
  Play, Trophy, Award, Clock, BookOpen, ChevronRight,
  Star, Zap, Target, Gift, Crown, Medal, GraduationCap,
  PartyPopper, Check, X, RefreshCw, Home, Share2
} from 'lucide-react';

// Icon mapping
const iconMap = {
  Sparkles: Sparkles,
  Users: Users,
  Scissors: Scissors,
  Building2: Building2,
  Shield: Shield
};

export default function SoldierAcademy() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentModuleData, setCurrentModuleData] = useState(null);
  const [view, setView] = useState('overview'); // overview, module, quiz, results, certificate
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [contentStep, setContentStep] = useState(0);

  const isSuperAdmin = user?.is_super_admin === true;

  useEffect(() => {
    if (token) {
      fetchModules();
      fetchProgress();
    }
  }, [token]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/academy/modules`);
      setModules(response.data.modules);
    } catch (error) {
      toast.error('Failed to load academy modules');
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/academy/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
      
      // If certified, show certificate
      if (response.data.is_certified && response.data.certificate_id) {
        // Don't auto-navigate, let them explore
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const startAcademy = async () => {
    try {
      await axios.post(`${API}/academy/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchProgress();
      toast.success('🎓 Academy started! You have 7 days to complete.');
    } catch (error) {
      toast.error('Failed to start academy');
    }
  };

  const loadModule = async (moduleId) => {
    try {
      const response = await axios.get(`${API}/academy/module/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentModuleData(response.data);
      setCurrentModule(moduleId);
      setContentStep(0);
      setQuizAnswers({});
      setQuizResults(null);
      setView('module');
    } catch (error) {
      toast.error('Failed to load module');
    }
  };

  const submitQuiz = async () => {
    if (!currentModule) return;
    
    // Check if all questions answered
    const quizLength = currentModuleData?.quiz?.length || 0;
    if (Object.keys(quizAnswers).length < quizLength) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmittingQuiz(true);
    try {
      const response = await axios.post(`${API}/academy/quiz/submit`, {
        module_id: currentModule,
        answers: quizAnswers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuizResults(response.data);
      setView('results');
      
      if (response.data.passed) {
        // Celebration!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        if (response.data.is_certified) {
          // Big celebration for full certification!
          setTimeout(() => {
            confetti({
              particleCount: 200,
              spread: 100,
              origin: { y: 0.5 }
            });
          }, 500);
          toast.success('🎖️ Congratulations! You are now a Certified Soldier!');
        } else {
          toast.success(`✅ Module passed! Score: ${response.data.score.toFixed(0)}%`);
        }
      } else {
        toast.error(`Score: ${response.data.score.toFixed(0)}% - Need ${response.data.passing_score}% to pass. Try again!`);
      }
      
      await fetchProgress();
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetAcademy = async () => {
    try {
      await axios.post(`${API}/academy/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchProgress();
      setView('overview');
      toast.success('Academy progress reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot reset academy');
    }
  };

  const getModuleStatus = (moduleId) => {
    if (!progress) return 'locked';
    if (progress.modules_completed?.includes(moduleId)) return 'completed';
    if (progress.current_module === moduleId) return 'current';
    
    // Check if previous module is completed
    const moduleIndex = modules.findIndex(m => m.id === moduleId);
    if (moduleIndex === 0) return 'available';
    
    const prevModule = modules[moduleIndex - 1];
    if (progress.modules_completed?.includes(prevModule?.id)) return 'available';
    
    return 'locked';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 flex items-center justify-center">
        <div className="text-center">
          <GraduationCap className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Loading Soldier Academy...</p>
        </div>
      </div>
    );
  }

  // Certificate View
  if (view === 'certificate' || (progress?.is_certified && view === 'overview')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
        {/* Header */}
        <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/soldiers/dashboard')} className="text-stone-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Command Center
              </Button>
            </div>
            {/* Allow ALL certified soldiers to retake academy */}
            <Button variant="outline" size="sm" onClick={resetAcademy} className="text-amber-400 border-amber-500/50">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retake Academy
            </Button>
          </div>
        </div>

        {/* Certificate */}
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="relative">
            {/* Decorative Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-3xl" />
            
            <Card className="relative bg-gradient-to-br from-stone-900 to-stone-950 border-2 border-amber-500/50 shadow-2xl shadow-amber-500/20 overflow-hidden">
              {/* Gold Corner Decorations */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-500/20 to-transparent" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-500/20 to-transparent" />
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-amber-500/20 to-transparent" />
              
              <CardContent className="p-8 sm:p-12 text-center relative z-10">
                {/* Badge */}
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/40">
                  <Medal className="w-12 h-12 text-black" />
                </div>
                
                <p className="text-amber-400 uppercase tracking-widest text-sm mb-2">Certificate of Completion</p>
                
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Soldier Academy</h1>
                <p className="text-stone-400 mb-8">MyHairChair Certified</p>
                
                <div className="mb-8">
                  <p className="text-stone-400 text-sm mb-1">This certifies that</p>
                  <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
                    {user?.name || 'HairChair Soldier'}
                  </p>
                </div>
                
                <p className="text-stone-300 max-w-md mx-auto mb-8">
                  has successfully completed all training modules and demonstrated proficiency in the MyHairChair platform, earning the title of
                </p>
                
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/50 rounded-full mb-8">
                  <Shield className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-bold text-lg">Certified Soldier</span>
                  <Trophy className="w-5 h-5 text-amber-400" />
                </div>
                
                <div className="flex items-center justify-center gap-8 text-sm text-stone-500 mb-8">
                  <div>
                    <p className="text-stone-400">Certificate ID</p>
                    <p className="font-mono text-amber-400">{progress?.certificate_id}</p>
                  </div>
                  <div>
                    <p className="text-stone-400">Issued</p>
                    <p className="text-white">{progress?.certified_at ? new Date(progress.certified_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                
                <p className="text-amber-400 font-medium">Keep the 100! 💯</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              onClick={() => navigate('/soldiers/dashboard')}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
            >
              <Shield className="w-4 h-4 mr-2" />
              Go to Command Center
            </Button>
            <Button
              variant="outline"
              onClick={() => setView('overview')}
              className="border-stone-600 text-stone-300"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Review Modules
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Results View
  if (view === 'results' && quizResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
        <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Button variant="ghost" size="sm" onClick={() => setView('overview')} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Modules
            </Button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            {quizResults.passed ? (
              <>
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {quizResults.is_certified ? '🎖️ Certified!' : '✅ Module Passed!'}
                </h1>
                <p className="text-green-400 text-xl font-bold">{quizResults.score.toFixed(0)}%</p>
              </>
            ) : (
              <>
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-400 to-rose-600 rounded-full flex items-center justify-center">
                  <X className="w-12 h-12 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">Not Quite!</h1>
                <p className="text-red-400 text-xl font-bold">{quizResults.score.toFixed(0)}%</p>
                <p className="text-stone-400 mt-2">Need {quizResults.passing_score}% to pass</p>
              </>
            )}
          </div>

          {/* Answer Review */}
          <Card className="bg-stone-900/50 border-stone-700 mb-8">
            <CardHeader>
              <CardTitle className="text-white">Answer Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {currentModuleData?.quiz?.map((q, idx) => {
                const userAnswer = quizAnswers[q.id];
                const correctAnswer = quizResults.correct_answers[q.id];
                const isCorrect = userAnswer === correctAnswer;
                
                return (
                  <div key={q.id} className={`p-4 rounded-lg border ${isCorrect ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className="flex items-start gap-3">
                      {isCorrect ? (
                        <Check className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium mb-2">{q.question}</p>
                        <p className={`text-sm ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                          Your answer: {q.options[userAnswer]}
                        </p>
                        {!isCorrect && (
                          <p className="text-sm text-green-400 mt-1">
                            Correct: {q.options[correctAnswer]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {quizResults.passed ? (
              quizResults.is_certified ? (
                <Button
                  onClick={() => setView('certificate')}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  View Certificate
                </Button>
              ) : (
                <Button
                  onClick={() => loadModule(quizResults.next_module)}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
                >
                  Next Module
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )
            ) : (
              <Button
                onClick={() => {
                  setQuizAnswers({});
                  setView('quiz');
                }}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setView('overview')}
              className="border-stone-600 text-stone-300"
            >
              Back to Overview
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz View
  if (view === 'quiz' && currentModuleData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
        <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setView('module')} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Content
            </Button>
            <Badge className="bg-amber-500/20 text-amber-400">
              {Object.keys(quizAnswers).length}/{currentModuleData.quiz?.length || 0} Answered
            </Badge>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">{currentModuleData.title} Quiz</h1>
            <p className="text-stone-400">Score 80% or higher to pass</p>
          </div>

          <div className="space-y-6">
            {currentModuleData.quiz?.map((q, idx) => (
              <Card key={q.id} className="bg-stone-900/50 border-stone-700">
                <CardContent className="p-6">
                  <p className="text-white font-medium mb-4">
                    <span className="text-amber-400 mr-2">{idx + 1}.</span>
                    {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((option, optIdx) => (
                      <button
                        key={optIdx}
                        onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: optIdx })}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          quizAnswers[q.id] === optIdx
                            ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                            : 'bg-stone-800/50 border-stone-700 text-stone-300 hover:border-stone-600'
                        }`}
                      >
                        <span className="mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                        {option}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Button
              onClick={submitQuiz}
              disabled={submittingQuiz || Object.keys(quizAnswers).length < (currentModuleData.quiz?.length || 0)}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold px-8"
            >
              {submittingQuiz ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Quiz
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Module Content View
  if (view === 'module' && currentModuleData) {
    const content = currentModuleData.content || [];
    const currentContent = content[contentStep];
    const isLastContent = contentStep >= content.length - 1;
    const IconComponent = iconMap[currentModuleData.icon] || Sparkles;

    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
        <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setView('overview')} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Modules
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-stone-400 text-sm">{contentStep + 1} / {content.length}</span>
              <Progress value={((contentStep + 1) / content.length) * 100} className="w-24 h-2" />
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Module Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${currentModuleData.color} flex items-center justify-center`}>
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{currentModuleData.title}</h1>
              <p className="text-stone-400 text-sm">{currentModuleData.duration}</p>
            </div>
          </div>

          {/* Content Card */}
          <Card className="bg-stone-900/50 border-stone-700 mb-8">
            <CardContent className="p-6 sm:p-8">
              {currentContent?.type === 'intro' && (
                <div className="text-center">
                  {/* Illustration for intro */}
                  {currentContent.image && (
                    <div className="mb-6">
                      <img 
                        src={currentContent.image} 
                        alt={currentContent.title}
                        className="w-48 h-48 sm:w-64 sm:h-64 mx-auto rounded-2xl object-cover shadow-lg shadow-amber-500/20"
                      />
                    </div>
                  )}
                  <h2 className="text-2xl font-bold text-white mb-4">{currentContent.title}</h2>
                  <p className="text-stone-300 text-lg leading-relaxed">{currentContent.text}</p>
                </div>
              )}
              {currentContent?.type === 'point' && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                      <Zap className="w-4 h-4 text-amber-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white">{currentContent.title}</h2>
                  </div>
                  <p className="text-stone-300 text-lg leading-relaxed pl-11">{currentContent.text}</p>
                  {/* Optional illustration for key points */}
                  {currentContent.image && (
                    <div className="mt-6 pl-11">
                      <img 
                        src={currentContent.image} 
                        alt={currentContent.title}
                        className="w-full max-w-sm rounded-xl object-cover shadow-lg"
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setContentStep(Math.max(0, contentStep - 1))}
              disabled={contentStep === 0}
              className="border-stone-600 text-stone-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            {isLastContent ? (
              <Button
                onClick={() => setView('quiz')}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
              >
                Take Quiz
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setContentStep(contentStep + 1)}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Overview View (default)
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
      {/* Header */}
      <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-stone-700" />
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-amber-400" />
              <span className="text-white font-semibold">Soldier Academy</span>
            </div>
          </div>
          {progress?.is_certified && (
            <Badge className="bg-green-500/20 text-green-400">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Certified
            </Badge>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
            <GraduationCap className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Soldier Academy
          </h1>
          <p className="text-stone-400 max-w-lg mx-auto">
            Complete your training to become a Certified HairChair Soldier and unlock your earning potential.
          </p>
          
          {/* Progress Stats */}
          {progress && (
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="px-4 py-2 bg-stone-800/50 rounded-lg">
                <p className="text-amber-400 text-2xl font-bold">{progress.overall_progress?.toFixed(0) || 0}%</p>
                <p className="text-stone-500 text-xs">Complete</p>
              </div>
              <div className="px-4 py-2 bg-stone-800/50 rounded-lg">
                <p className="text-white text-2xl font-bold">{progress.modules_completed?.length || 0}/{modules.length}</p>
                <p className="text-stone-500 text-xs">Modules</p>
              </div>
              {progress.deadline && !progress.is_certified && (
                <div className="px-4 py-2 bg-stone-800/50 rounded-lg">
                  <p className="text-red-400 text-2xl font-bold">
                    {Math.max(0, Math.ceil((new Date(progress.deadline) - new Date()) / (1000 * 60 * 60 * 24)))}
                  </p>
                  <p className="text-stone-500 text-xs">Days Left</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Start Button (if not started) */}
        {!progress?.started_at && !progress?.is_certified && (
          <div className="text-center mb-12">
            <Button
              onClick={startAcademy}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold px-8"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Academy
            </Button>
            <p className="text-stone-500 text-sm mt-3">You'll have 7 days to complete all modules</p>
          </div>
        )}

        {/* Overall Progress Bar */}
        {progress?.started_at && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stone-400 text-sm">Overall Progress</span>
              <span className="text-amber-400 font-medium">{progress.overall_progress?.toFixed(0) || 0}%</span>
            </div>
            <Progress value={progress.overall_progress || 0} className="h-3" />
          </div>
        )}

        {/* Modules Grid */}
        <div className="space-y-4">
          {modules.map((module, idx) => {
            const status = getModuleStatus(module.id);
            const IconComponent = iconMap[module.icon] || Sparkles;
            const score = progress?.quiz_scores?.[module.id];
            
            return (
              <Card 
                key={module.id}
                className={`bg-stone-900/50 border transition-all cursor-pointer overflow-hidden ${
                  status === 'locked' 
                    ? 'border-stone-800 opacity-60' 
                    : status === 'completed'
                      ? 'border-green-500/30 hover:border-green-500/50'
                      : 'border-stone-700 hover:border-amber-500/50'
                }`}
                onClick={() => status !== 'locked' && loadModule(module.id)}
              >
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Module Image */}
                    {module.image && (
                      <div className="hidden sm:block w-24 h-24 shrink-0">
                        <img 
                          src={module.image} 
                          alt={module.title}
                          className={`w-full h-full object-cover ${status === 'locked' ? 'grayscale opacity-50' : ''}`}
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-4 p-4 sm:p-5 flex-1">
                      {/* Module Number/Status */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        status === 'completed' 
                          ? 'bg-green-500/20' 
                          : status === 'locked'
                            ? 'bg-stone-800'
                            : `bg-gradient-to-br ${module.color}`
                      }`}>
                        {status === 'completed' ? (
                          <CheckCircle2 className="w-6 h-6 text-green-400" />
                        ) : status === 'locked' ? (
                          <Lock className="w-5 h-5 text-stone-600" />
                        ) : (
                          <IconComponent className="w-6 h-6 text-white" />
                        )}
                      </div>
                    
                      {/* Module Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-semibold ${status === 'locked' ? 'text-stone-500' : 'text-white'}`}>
                            Module {idx + 1}: {module.title}
                          </h3>
                          {status === 'current' && (
                            <Badge className="bg-amber-500 text-black text-xs">Current</Badge>
                          )}
                        </div>
                        <p className={`text-sm ${status === 'locked' ? 'text-stone-600' : 'text-stone-400'}`}>
                          {module.description}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {module.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3 h-3" />
                            {module.quiz_count} questions
                          </span>
                          {score !== undefined && (
                            <span className={`flex items-center gap-1 ${score >= 80 ? 'text-green-400' : 'text-red-400'}`}>
                              <Star className="w-3 h-3" />
                              {score.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      {status !== 'locked' && (
                        <ChevronRight className="w-5 h-5 text-stone-600" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Certification Info */}
        <Card className="mt-8 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                <Trophy className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Earn Your Certified Soldier Badge</h3>
                <p className="text-stone-400 text-sm">
                  Complete all 5 modules with 80% or higher on each quiz. Upon certification, you'll unlock full access to the Soldier Command Center, your referral tools, and commission tracking.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
